import { DatePipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  model,
  OnInit,
  TemplateRef,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FollowUpsUtilsService } from '@app/modules/follow-ups/services/follow-ups-utils.service';
import { ReportsUtilsService } from '@app/modules/reports/services/reports-utils.service';
import { ResultsUtilsService } from '@app/modules/reports/services/results-utils.service';
import {
  GetReportAction,
  GetReportsListAction,
} from '@app/modules/reports/store/reports.actions';
import { ReportsState } from '@app/modules/reports/store/reports.state';
import {
  ReportStatus,
  ReportType,
} from '@app/modules/reports/types/report.types';
import { ProductInventoryItemUtilsService } from '@app/modules/service-data/services/product-inventory-item-utils.service';
import { LanguageState } from '@core/store/languages/languages.state';
import { LoadPartnerById } from '@core/store/partners/partners.actions';
import { PartnersState } from '@core/store/partners/partners.state';
import { ProductsListState } from '@core/store/products/products.state';
import { SchemeState } from '@core/store/schemes/schemes.state';
import { SigneeState } from '@core/store/signees/signees.state';
import { TemplatesState } from '@core/store/templates/templates.state';
import { CoreUtilsService } from '@core/utils/core-utils.service';
import { Util } from '@core/utils/core.util';
import { NgbActiveModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { DataTableComponent } from '@shared/components/data-table/data-table.component';
import {
  DataTableActionsLocation,
  DataTableActionType,
  DataTableConfiguration,
  DataTableDataType,
  DataTableSelectionType,
} from '@shared/components/data-table/data-table.types';
import { RequirePermissionDirective } from '@shared/directives/require-permission.directive';
import { map } from 'rxjs';

@Component({
  selector: 'app-report-details-dialog',
  imports: [
    NgbTooltip,
    TranslatePipe,
    DatePipe,
    DataTableComponent,
    RequirePermissionDirective,
  ],
  templateUrl: './report-details-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './report-details-dialog.component.scss',
})
export class ReportDetailsDialogComponent implements OnInit {
  private store = inject(Store);
  private modal = inject(NgbActiveModal);
  protected coreUtilsService = inject(CoreUtilsService);
  protected followUpsUtils = inject(FollowUpsUtilsService);
  protected reportsUtils = inject(ReportsUtilsService);
  protected piiUtils = inject(ProductInventoryItemUtilsService);
  protected resultsUtilsService = inject(ResultsUtilsService);

  public reportId = model<string>(null);

  protected report = computed(() =>
    this.store.selectSignal(ReportsState.getReport)()(this.reportId())
  );
  protected properties = computed(() => {
    return Util.PropertiesToObject(this.report().properties);
  });
  protected product = computed(() => {
    return this.store.selectSignal(ProductsListState.getProduct)()(
      this.properties().productid
    );
  });
  protected partner = computed(() => {
    return this.store.selectSignal(PartnersState.getPartnerById)()(
      Util.PropertiesToObject(this.report().properties).partnerid
    );
  });
  protected template = computed(() => {
    return this.store.selectSignal(TemplatesState.getTemplateById)()(
      this.report().templateId
    );
  });
  protected scheme = computed(() => {
    return this.store.selectSignal(SchemeState.getSchemeById)()(
      this.product().schemeId
    );
  });
  protected reportLanguageById = this.store.selectSignal(
    LanguageState.getLanguageById
  );
  protected signeeById = this.store.selectSignal(SigneeState.getSigneeById);

  protected connectedReports = computed(() =>
    this.store.selectSignal(ReportsState.getReportsByRootId)()(this.rootId())
  );

  connectedReports$ = toObservable(this.connectedReports).pipe(
    map((reports) => reports.filter((report) => report.id !== this.reportId()))
  );

  protected rootId = computed(() => this.report()?.rootId ?? null);

  protected invalidValues = computed(() => this.report()?.invalidValues || []);
  invalidValues$ = toObservable(this.invalidValues);

  protected failedReasonDataTableConfiguration: DataTableConfiguration;
  protected connectedReportsDataTable: DataTableConfiguration;

  protected statusTemplate = viewChild<TemplateRef<any>>('statusTemplate');

  constructor() {
    effect(() => {
      this.store.dispatch(new GetReportAction(this.reportId()));
    });

    effect(() => {
      this.store.dispatch(
        new GetReportsListAction({ rootIds: [this.rootId()] }, {}, null, null)
      );
    });

    effect(() => {
      if (this.report()) {
        this.store.dispatch(
          new LoadPartnerById(
            Util.PropertiesToObject(this.report().properties)?.partnerid,
            false
          )
        );
      }
    });
  }

  ngOnInit(): void {
    this.failedReasonDataTableConfiguration = {
      title: '',
      selectionMode: DataTableSelectionType.NONE,
      dynamicPageSize: true,
      columns: [
        {
          label: 'reports.details.invalidValues.field',
          property: 'field',
        },
        {
          label: 'reports.details.invalidValues.value',
          property: 'value',
        },
      ],
      rows$: this.invalidValues$,
    };

    this.connectedReportsDataTable = {
      title: '',
      selectionMode: DataTableSelectionType.NONE,
      actionsLocation: DataTableActionsLocation.RIGHT,
      singleActions: [
        {
          name: 'reports.details.tooltips.openReport',
          icon: 'fa-light fa-file',
          action: (event, report) =>
            this.reportsUtils.openReportDetailsDialog(report.id),
          type: DataTableActionType.BUTTON,
        },
      ],
      columns: [
        {
          label: 'reports.list.columns.kitId',
          invoke: {
            objectReference: {
              fn: (report: any) =>
                report.properties?.find((p) => p.key === 'KitId')?.value,
            },
            name: 'fn',
            args: [],
          },
        },
        {
          label: 'reports.list.columns.analysisType',
          invoke: {
            objectReference: {
              fn: (report: any) =>
                report.properties?.find((p) => p.key === 'AnalysisType')?.value,
            },
            name: 'fn',
            args: [],
          },
        },
        {
          label: 'reports.list.columns.version',
          invoke: {
            objectReference: {
              fn: (report: any) => 'v' + report.version,
            },
            name: 'fn',
            args: [],
          },
        },
        {
          label: 'reports.list.columns.reportType',
          property: 'reportType',
        },
        {
          label: 'reports.list.columns.reportReason',
          property: 'reportReason',
        },
        {
          label: 'reports.list.columns.generated',
          property: 'generated',
          type: DataTableDataType.DATETIME,
        },
        {
          label: 'reports.list.columns.status',
          propertyTemplate: this.statusTemplate(),
        },
      ],
      rows$: this.connectedReports$,
    };
  }

  protected dismiss() {
    this.modal.dismiss();
  }

  protected openReportDetails(reportId: string) {
    this.reportsUtils.openReportDetailsDialog(reportId);
  }

  protected readonly ReportStatus = ReportStatus;
  protected readonly Util = Util;
  protected readonly ReportType = ReportType;
}
